import {
  useProductBase,
  useProductVariant,
  useVariantMissingDimensions,
  useProductOptions,
  useProductVariants,
  getVariantFromOptions,
} from "@made-people/grease-product";
import { useTranslate } from "@made-people/grease-translations";
import { useCountriesAndLanguages } from "@made-people/grease-countries-and-languages";
import { getVariantStockStatus } from "./out-of-stock-information";
import { getVariantStockDeliveryData } from "./get-stock-delivery-data";
import { ProductDeliveryStatus } from "./product-delivery-status";
import { mmProductIsFixedBundle } from "../../../services/centra/mappers/mm-product-extra-attributes";

export const WithVariantsProductDeliveryStatus = () => {
  const productBase = useProductBase();
  const translate = useTranslate();
  const variant = useProductVariant();
  const missingDimensions = useVariantMissingDimensions();
  const { selectedOptions } = useProductOptions();
  const variants = useProductVariants();
  const { currentCountryCode } = useCountriesAndLanguages();
  const variantStockData = getVariantStockDeliveryData(
    productBase,
    currentCountryCode,
  );

  if (mmProductIsFixedBundle(productBase)) {
    /**
     * If the product is fixed bundle and a size has been selected,
     * we know that the product is in stock.
     */
    return (
      <ProductDeliveryStatus
        discontinued={variantStockData?.discontinued ?? false}
        status={translate("Delivery_In_Stock")}
        inStock={variant.inventory.inStock}
        isImmediateDelivery={true}
      />
    );
  }

  // Build size label from selected options (e.g., "105xH") instead of using variant.label
  // This ensures we look up the actually selected size, not the fallback variant
  const selectedSizeLabel = Object.values(selectedOptions)
    .map((opt) => opt?.dimensionValue)
    .filter(Boolean)
    .join("x");

  // Check if selected options match any actual variant
  const hasMatchingVariant =
    missingDimensions.length > 0 ||
    getVariantFromOptions(variants, selectedOptions) !== undefined;

  if (!variantStockData || missingDimensions.length) {
    return <div className="min-h-5" />;
  }

  // If selected size combination doesn't exist, show invalid combination message
  if (!hasMatchingVariant) {
    return (
      <ProductDeliveryStatus
        discontinued={variantStockData.discontinued}
        status={translate("Delivery_Invalid_Size_Combination")}
        inStock={false}
        isImmediateDelivery={false}
      />
    );
  }

  // Use selectedSizeLabel to look up stock status for the actually selected size
  const { deliveryStatus, isImmediateDelivery } = getVariantStockStatus(
    variant,
    variantStockData,
    selectedSizeLabel || variant.label,
    translate,
  );

  return (
    <ProductDeliveryStatus
      discontinued={variantStockData.discontinued}
      status={deliveryStatus}
      inStock={variant.inventory.inStock}
      isImmediateDelivery={isImmediateDelivery}
    />
  );
};
